// PAB Benefits — scroll-driven scene switching (desktop) and in-view video playback (mobile).
class PabBenefits extends HTMLElement {
  #cleanup = [];
  #teardownMode = null;

  connectedCallback() {
    this.mq = window.matchMedia('(max-width: 900px)');
    const onChange = () => this.#setup();
    this.mq.addEventListener('change', onChange);
    this.#cleanup.push(() => this.mq.removeEventListener('change', onChange));
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup.forEach((fn) => fn());
    this.#cleanup = [];
    this.#teardownMode?.();
  }

  #setup() {
    this.#teardownMode?.();
    this.#pauseAll();
    this.#teardownMode = this.mq.matches ? this.#setupMobile() : this.#setupDesktop();
  }

  #pauseAll() {
    this.querySelectorAll('video').forEach((v) => v.pause());
  }

  #play(video) {
    if (!video) return;
    if (video.preload === 'none') video.preload = 'auto';
    const attempt = () => video.play().catch(() => {});
    attempt();
    if (video.readyState < 3) video.addEventListener('canplay', attempt, { once: true });
  }

  /* Desktop: nearest-to-center step drives the sticky stage. */
  #setupDesktop() {
    const steps = [...this.querySelectorAll('[data-step-index]')];
    const scenes = [...this.querySelectorAll('[data-scene-index]')];
    if (!steps.length) return () => {};

    let active = -1;
    let raf = null;

    const apply = (i) => {
      if (i === active) return;
      active = i;
      steps.forEach((s, j) => s.classList.toggle('on', j === i));
      scenes.forEach((s, j) => {
        s.classList.toggle('on', j === i);
        const v = s.querySelector('video');
        if (v) j === i ? this.#play(v) : v.pause();
      });
    };

    const update = () => {
      raf = null;
      const mid = window.innerHeight / 2;
      let best = 0;
      let bd = Infinity;
      steps.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        const d = Math.abs((r.top + r.bottom) / 2 - mid);
        if (d < bd) { bd = d; best = i; }
      });
      apply(best);
    };
    const onScroll = () => { if (raf === null) raf = requestAnimationFrame(update); };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }

  /* Mobile: play each card's video while it's in view. */
  #setupMobile() {
    const videos = [...this.querySelectorAll('[data-pab-mobile] video')];
    if (!videos.length) return () => {};

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          e.isIntersecting ? this.#play(e.target) : e.target.pause();
        });
      },
      { threshold: 0.4 }
    );
    videos.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }
}

if (!customElements.get('pab-benefits')) customElements.define('pab-benefits', PabBenefits);
